import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { property, query, queryAssignedElements } from 'lit/decorators.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-dialog.styles';
import { SheriffEvent } from '../types';
import { SDSEventDispatcherMixin } from '../mixins/sds-event-dispatcher-mixin';

import '../sds-icon/sds-icon';
import '../sds-button/sds-button';

type ClosedBy = 'none' | 'closerequest' | 'any';

export interface SDSDialogProps {
	open?: boolean;
	/**
	 * Set if dialog opens as a modal or not
	 *
	 * If non-modal, the user is allowed to interact with content outside of the dialog.
	 * If modal, the dialog is placed in the top layer with a ::backdrop pseudo-element.
	 *
	 * Docs:
	 * - https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/show
	 * - https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/showModal
	 */
	modal?: boolean;
	/**
	 * Set what type of dismiss behavior closes the dialog
	 * - 'none': Dialog can only be closed programmatically
	 * - 'closerequest': Dialog closes on ESC key (default)
	 * - 'any': Dialog closes on ESC key or clicking outside
	 *
	 * Docs: https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/closedBy
	 */
	closedby?: ClosedBy;
	'close-button-label'?: string;
}

@guardedCustomElement('sds-dialog')
export class SDSDialog extends SDSEventDispatcherMixin(LitElement) {
	static override styles = [styles];

	@property({ type: Boolean, reflect: true }) open: boolean = false;
	@property({ type: Boolean, reflect: true }) modal: boolean = false;
	@property({ type: String, reflect: true }) closedby: ClosedBy = 'closerequest';
	@property({ type: String, attribute: 'close-button-label' }) closeButtonLabel: string = 'Lukk dialogvindu';

	@query('dialog') private _dialog!: HTMLDialogElement;

	@queryAssignedElements({ slot: 'header', flatten: false }) private headerSlotElements!: Element[];

	// Safari closedBy=any polyfill state
	private _pointerDownInside = false;

	override firstUpdated(): void {
		this._setupEventListeners();
		if (this.open) {
			this._openDialog();
		}
	}

	override updated(changedProperties: PropertyValues): void {
		if (!changedProperties.has('open')) {
			return;
		}

		if (this.open) {
			this._openDialog();
		} else {
			this._closeDialog();
		}
	}

	private _setupEventListeners(): void {
		this._dialog.addEventListener('close', this._handleClose);
		this._dialog.addEventListener('cancel', this._handleCancel);
		this._dialog.addEventListener('click', this._handleClick);
		this._dialog.addEventListener('pointerdown', this._handlePointerDown);
		this._dialog.addEventListener('pointerup', this._handlePointerUp);
		this._dialog.addEventListener('animationend', this._handleAutoFocus);
	}

	private _openDialog(): void {
		if (!this._dialog) {
			return;
		}

		if (this.modal) {
			this._dialog.showModal();
		} else {
			this._dialog.show();
		}
	}

	private _closeDialog(): void {
		if (!this._dialog) {
			return;
		}
		this._dialog.close();
	}

	private readonly _handleClose = (): void => {
		this.open = false;
		this.dispatchSheriffEvent('sds-close');
	};

	private readonly _handleClick = (event: MouseEvent): void => {
		const target = event.target as Element | null;

		if (target?.closest('[data-command="close"]')) {
			this._closeDialog();
		}
	};

	// Safari closedBy="any" polyfill
	// Calculate whether the user's pointer down event is outside or inside dialog
	// If outside, close the dialog
	private readonly _handlePointerDown = (event: PointerEvent): void => {
		const rect = this._dialog.getBoundingClientRect();
		const { clientX: x, clientY: y } = event;
		this._pointerDownInside = rect.top <= y && y <= rect.bottom && rect.left <= x && x <= rect.right;
	};

	private readonly _handlePointerUp = (event: PointerEvent): void => {
		const wasDownInside = this._pointerDownInside;
		this._pointerDownInside = false;

		if (this.closedby !== 'any' || wasDownInside) {
			return;
		}

		// Clicking the backdrop will have the dialog as the target in Safari/Chrome.
		// Firefox does not fire this callback when ::backdrop is clicked.
		if (event.target !== this._dialog) {
			return;
		}

		// Use requestAnimationFrame to let the browser handle native closedBy first.
		// This avoids checking for the closedBy property, which Safari 26.2 exposes
		// without implementing.
		requestAnimationFrame(() => {
			if (this._dialog && this._dialog.open) {
				this._closeDialog();
			}
		});
	};

	private readonly _handleCancel = (event: Event): void => {
		if (this.closedby === 'none') {
			event.preventDefault();
		}
	};

	private readonly _handleAutoFocus = (): void => {
		// slot:not([name]) finds the default nameless slot
		const contentSlotElements = this.shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])')?.assignedElements({ flatten: true });
		if (!contentSlotElements || contentSlotElements.length === 0) {
			return;
		}

		const elementToFocus = contentSlotElements.find((element) => {
			return element.getAttribute('autofocus') != null;
		});

		if (!(elementToFocus instanceof HTMLElement)) {
			return;
		}

		if (document.activeElement !== elementToFocus) {
			elementToFocus.focus();
		}
	};

	public show(): void {
		this.open = true;
	}

	public showModal(): void {
		this.modal = true;
		this.open = true;
	}

	public close(): void {
		this.open = false;
	}

	override render() {
		const headerIsEmpty = this.headerSlotElements.length === 0;
		return html`
			<dialog .closedBy="${this.closedby}" class=${headerIsEmpty ? 'sds-dialog__empty-header' : ''}>
				<div class="sds-dialog__header">
					<slot name="header"></slot>
					${this.closeButtonLabel
						? html`
								<sds-button class="sds-dialog__close-button" icon variant="tertiary" size="md" data-command="close" aria-label="${this.closeButtonLabel}">
									<sds-icon name="close-material-outlined" size="md"></sds-icon>
								</sds-button>
							`
						: nothing}
				</div>
				<slot></slot>
				<slot name="footer"></slot>
			</dialog>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-dialog': SDSDialog;
	}
	interface GlobalEventHandlersEventMap {
		'sds-close': SheriffEvent;
	}
}
